/**
 * BatchState store (008 data-model, 005) — selected template, uploaded designs,
 * local render progress, outputs/failures.
 *
 * Progress is AGGREGATE across a whole run. A multi-template batch enqueues one
 * queue of templates × designs (many jobs); `expectedTotal` is the run's total
 * item count (set at enqueue), finished items are `outputs + failures`, and
 * `currentByJob` holds each in-flight job's partial (from per-job onProgress), keyed by
 * jobId so concurrency > 1 can't corrupt the aggregate; `currentCompleted` is their sum.
 * So the bar advances monotonically over the entire run instead of resetting to
 * 0 at each job boundary. With no `expectedTotal` set, it falls back to the
 * single-job progress reported via `setProgress` (the original behavior).
 */
import { create } from "zustand";
import type { BatchResult, DesignInput, OutputRecord, PrintFileRef, ViewName, LocalStore } from "@genz/platform";
import { currentPlatform } from "@genz/platform";
import { getOutputBytesBudgetMb } from "./memoryBudget.js";
import type { ExportReport } from "../export/bundle.js";

/**
 * KV key for the operator's chosen output folder. It is DEVICE-LOCAL by design (a filesystem
 * path only means something on this machine), so it persists to `LocalStore.kv` — never to the
 * cloud — and is rehydrated on boot via `restoreSaveDir` (called from createAppServices).
 */
export const SAVE_DIR_KEY = "genz.batch.saveDir";

function saveDirKv(store?: LocalStore) {
  return (store ?? currentPlatform()?.localStore)?.kv;
}

/**
 * Persist the chosen folder. Fire-and-forget so it never blocks the UI update, but NOT silent:
 * a failed write (or a missing kv) meant the folder the operator picked for the run was gone on
 * the next launch and the next export wrote somewhere else with no warning (release-audit class
 * 6). The console is the only sink this module has — it is a store, below the logger — and one
 * line there is what makes the loss traceable at all.
 */
function persistSaveDir(dir: string | undefined): void {
  const kv = saveDirKv();
  if (!kv) {
    console.warn("[batch] no local key-value store — the chosen save folder will not survive a restart");
    return;
  }
  const failed = (e: unknown) =>
    console.warn("[batch] the chosen save folder could not be persisted; it will not survive a restart", e);
  if (dir) void kv.set(SAVE_DIR_KEY, dir).catch(failed);
  else void kv.delete(SAVE_DIR_KEY).catch(failed);
}

/** Rehydrate the persisted save folder into the store on boot (no-op when none stored). */
export async function restoreSaveDir(store?: LocalStore): Promise<void> {
  const kv = saveDirKv(store);
  if (!kv) return;
  const dir = await kv.get<string>(SAVE_DIR_KEY);
  if (dir) useBatchStore.setState({ saveDir: dir });
}

/**
 * The per-run output folder, `<yyyy-MM-dd HH-mm>` in local time (D-13).
 *
 * AUD-3 LB-4: the bundle root was the constant "Mockups", the destination was the REMEMBERED
 * save folder, and `writeFile` has no existence check — so re-rendering the same designs at a
 * different size silently replaced the first run's files with no prompt, no backup and no
 * report line. Versioning by run makes the second export additive; "replace previous run"
 * brings the old behavior back for operators who want one canonical folder.
 */
export function runFolderName(at: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${at.getFullYear()}-${p(at.getMonth() + 1)}-${p(at.getDate())} ${p(at.getHours())}-${p(at.getMinutes())}`;
}

function aggregate(
  expectedTotal: number,
  finished: number,
  currentCompleted: number,
  fallback: { completed: number; total: number },
): { completed: number; total: number } {
  if (expectedTotal <= 0) return fallback;
  return { completed: Math.min(expectedTotal, finished + currentCompleted), total: expectedTotal };
}

/**
 * The in-flight partial across ALL running jobs.
 *
 * This used to be a single scalar shared by every job. With concurrency > 1 each job's
 * `onProgress` stomped the others' value, so the aggregate bar oscillated and — worse —
 * `failJob` subtracted a sibling job's count from the run total. Keyed by `jobId`, each
 * job contributes its own partial and only its own entry is cleared when it lands.
 */
const SINGLE_JOB_KEY = "__single__";

/** One rendered print file as it rides the store (bytes + the template it belongs to). */
type StoredPrintFile = PrintFileRef & { bytes?: Uint8Array; templateId?: string };

/**
 * Collapse the print files to ONE per (template, design), last wins (review-1 M-4).
 *
 * `appendResult` concatenates, so a retried back view pushed a SECOND entry for the same
 * design. `printFilesFromOutputs` keys its map the same way, so the export already shipped
 * only the last one — but the duplicate's multi-MB bytes stayed resident for the rest of the
 * run, and a retry-heavy run accumulated one leak per retry.
 *
 * Keyed on (template, design) rather than design alone for the same reason that map is: each
 * template writes its own `Print/` folder, so the same design under two templates is two
 * different files. The entry is re-inserted at the END so the retry counts as the NEWEST for
 * the spill's newest-first walk.
 */
function dedupePrintFiles(list: StoredPrintFile[]): StoredPrintFile[] {
  const byKey = new Map<string, StoredPrintFile>();
  for (const p of list) {
    const key = `${p.templateId ?? ""}::${p.designSlug}`;
    byKey.delete(key);
    byKey.set(key, p);
  }
  return [...byKey.values()];
}
function partialSum(byJob: Record<string, number>): number {
  let n = 0;
  for (const k in byJob) n += byJob[k];
  return n;
}
function withoutJob(byJob: Record<string, number>, jobId: string): Record<string, number> {
  if (!(jobId in byJob)) return byJob;
  const next = { ...byJob };
  delete next[jobId];
  return next;
}

interface BatchState {
  templateId?: string;
  versionId?: string;
  designs: DesignInput[];
  /** Design ids that WILL render (checkbox = include). Defaults to all on setDesigns. */
  selectedDesignIds: string[];
  /** Operator-chosen output folder (desktop path), remembered across runs. */
  saveDir?: string;
  /** The folder the LAST export actually wrote to (desktop). Known from item 1, not from the
   *  end of the run, and shown with an "Open folder" action on the Batch panel's save-folder
   *  row, in its action bar and in the batch-details popup — so the destination is verifiable
   *  DURING the run as well as after it, and a count alone is no longer the only evidence.
   *  (The AUTOMATIC reveal is a different thing and waits for the run to end: `runWriter.ts`.) */
  lastSavedPath?: string;
  /**
   * Job id → the folder THAT job's run wrote into.
   *
   * `lastSavedPath` above is ONE slot shared by both arms (`app/bootstrap.ts::onOutput` stamps
   * it for a mock-up run and for a Transparent conversion alike), which is right for the panel
   * that is running the job and wrong for a LIST of runs: after one conversion, every Queue
   * row's "Open folder" pointed at the overlay folder (review F-4). A row asks this map for its
   * own jobs' folder instead, and offers nothing when none of them has written yet.
   *
   * Every job of a multi-template run stamps the SAME folder — they share one writer — so the
   * batch row can read whichever of its jobs answers first. It is kept across `reset()` for the
   * reason `lastSavedPath` is: it is where a run that ALREADY happened wrote, and the Queue
   * still lists those runs. One short string per job of the session.
   */
  savedPathByJob: Record<string, string>;
  /**
   * The output preset chosen on the Batch tab ("" = workspace default). Lives HERE for the
   * same reason `selectedDesignIds` does: ExportWorkspace mounts the Batch panel only while
   * its sub-tab is active, so component state was wiped by a trip to Queue and the run then
   * silently fell back to the workspace defaults.
   */
  outputPresetName: string;
  /**
   * Export tab: the `templateId::designSlug` keys the operator marked as approved selects.
   * Component state was reset by every sub-tab switch, so "Approve N selects" came back
   * reading 0 — and its no-marks fallback shipped EVERYTHING. Cleared when a new run arms.
   */
  markedSelects: string[];
  views: ViewName[];
  progress: { completed: number; total: number };
  /** When the current run armed (`clearOutputs`), and when its progress settled; the run's clock. */
  runStartedAt?: number;
  runEndedAt?: number;
  /**
   * Total items expected across the whole run. This MUST cover every item that will ever be
   * counted in `outputs + failures` — including designs that are SKIPPED before the engine
   * sees them (byte-less artwork is appended as a failure row up front). Counting those
   * failures while excluding them from the total let `outputs+failures >= expectedTotal`
   * trip mid-run, which released the Render button while jobs were still rendering; the
   * re-click's `clearOutputs()` then wiped the live run's results. 0 = single-job mode.
   */
  expectedTotal: number;
  /** Per-job in-flight completed counts, keyed by jobId. See the `partialSum` note. */
  currentByJob: Record<string, number>;
  /** Sum of `currentByJob` — the run-wide in-flight partial (read-only mirror). */
  currentCompleted: number;
  outputs: OutputRecord[];
  /**
   * WS-H2 / D-10: the RENDERED print files (back print area at `render_settings.print_dpi`),
   * accumulated per job exactly like `outputs`. Empty on web and on any template that is
   * not a print-file source — the export then ships the customer's uploaded artwork, as it
   * always did.
   *
   * They carry multi-MB bytes (a photographic design at 600 DPI is ~2 MB of PNG), so
   * `spillSavedOutputs` stamps and drops them under the SAME output-bytes budget the mockups
   * use, and `bundle.ts::hydrateOutputBytes` reads a dropped one back from `savedRoot` at
   * export time (review-1 I-1 — this comment used to claim the opposite of the code).
   * Deduped per (template, design) by `appendResult`, so a retry replaces rather than
   * accumulates (review-1 M-4).
   */
  printFiles: (PrintFileRef & { bytes?: Uint8Array; templateId?: string })[];
  failures: BatchResult["failures"];
  // WS-O / D-36: `autoSavedRun` + `markAutoSaved` are GONE. The latch existed to stop a
  // `BatchPanel` remount over a finished run re-firing the ONE end-of-run save (and re-opening
  // the folder). With write-through there is no end-of-run save to re-fire — each item is
  // written when it finishes, by one writer that is not a React effect — so the latch guarded
  // nothing. `addExpected`'s re-arm went with it; its ARITHMETIC is untouched.
  /**
   * A whole-run failure the operator must see (a swallowed job-level engine throw, or a
   * save-folder cancel). Set → the Batch panel shows a dismissible banner and releases the
   * "Rendering…" button so the run isn't a silent hang. `null` = no active error.
   */
  jobError: string | null;
  /**
   * The dated subfolder THIS run exports into (`<yyyy-MM-dd HH-mm>`), stamped once by
   * `clearOutputs()` — the single place a run arms. Every export surface (auto-save, a later
   * "Save to folder", the Export tab) reads it, so one run writes into ONE folder.
   * `undefined` when "replace previous run" is on: exports go straight into `Mockups/`.
   */
  runFolder?: string;
  /** Opt back into the pre-WS-H1 behavior: every run overwrites `Mockups/` (AUD-3 LB-4). */
  replacePreviousRun: boolean;
  /**
   * WS-O / O-8 (WS-N review-1 M-4): what the WEB run has staged for the operator's click.
   *
   * There is no folder in a browser, so "each file lands as it finishes" is not achievable and
   * this wave does not pretend otherwise. What IS achievable: the run's writer accumulates each
   * finished file, `count` climbs live so the operator can watch the run fill, and the bytes are
   * handed to the browser only on a CLICK — a zip download fired from job completion has no user
   * gesture behind it (Chrome allows a programmatic anchor download; Safari's behaviour is not
   * guaranteed).
   *
   * `entries` reads THROUGH to the writer rather than copying: web cannot spill, so the bytes
   * are already the run's peak and a second copy would double it. Cleared when a run arms.
   */
  webZip?: { count: number; entries: () => { relPath: string; bytes: Uint8Array }[] };
  /**
   * WS-O review-1 B-1: the RUN's own `ExportReport`, as the run writer has it — how many files
   * it put on disk, and every ref it could NOT (`failed` / `collisions` / `missing`).
   *
   * It exists because `RunWriter.report()` had no panel consumer at all: a 1000-item run that
   * went disk-full at item 200 finished "1000/1000" with a full gallery, 200 files in the folder
   * and NOT ONE word on any surface — the only trace was 800 lines in the Error log. The brief's
   * rule is "a write failure per item → visible row state + error log, never swallowed"; this is
   * what the visible half reads.
   *
   * A SNAPSHOT, not the writer's own object: that one is mutated per item and released when the
   * run's last job goes terminal, so a panel holding it would hold a moving target and then a
   * disposed one. Published by `app/bootstrap.ts::onOutput` whenever the tally changes, and
   * cleared when the next run arms (`clearOutputs`).
   */
  runReport?: ExportReport;
  setTemplate(templateId: string, versionId: string, views: ViewName[]): void;
  setDesigns(designs: DesignInput[]): void;
  /**
   * APPEND designs to the staged list, preserving the operator's existing tick state and
   * skipping anything already staged (same filename + source path, or same slug). Returns
   * how many were actually added.
   *
   * "+ Add" used to call `setDesigns`, which REPLACED the list and re-ticked every design
   * the operator had unticked — so a second import silently re-armed excluded artwork.
   */
  addDesigns(designs: DesignInput[]): number;
  /** Replace the render-selection (which staged designs will render). */
  setSelectedDesigns(ids: string[]): void;
  /** Remember (or clear) the chosen output folder across runs. */
  setSaveDir(dir: string | undefined): void;
  /** Record where the last export ACTUALLY wrote (see `lastSavedPath`). */
  setLastSavedPath(path: string | undefined): void;
  /** Record where THIS job's run wrote (see `savedPathByJob`). Idempotent: a run stamps the
   *  same folder once per item, and re-setting an identical value must not re-render. */
  setJobSavedPath(jobId: string, path: string): void;
  /** Choose the run's output preset by name ("" = workspace default). */
  setOutputPreset(name: string): void;
  /** Replace the Export tab's marked-selects set. */
  setMarkedSelects(keys: string[]): void;
  /** Toggle "replace previous run" (see {@link BatchState.replacePreviousRun}). */
  setReplacePreviousRun(on: boolean): void;
  /** WEB only: publish the run writer's staged zip entries (see {@link BatchState.webZip}). */
  setWebZip(zip: BatchState["webZip"]): void;
  /** Publish the run writer's tally so a panel can render it (see {@link BatchState.runReport}). */
  setRunReport(report: ExportReport | undefined): void;
  /** Clear a run's outputs/progress but KEEP designs, selection, and saveDir (no reset). */
  clearOutputs(): void;
  /** Declare the run's total item count (multi-job aggregate progress). */
  setExpectedTotal(total: number): void;
  /** Report one job's in-flight partial. `jobId` keys it so concurrent jobs don't stomp
   *  each other; omit it only in single-job/legacy callers. */
  setProgress(completed: number, total: number, jobId?: string): void;
  setResult(result: BatchResult): void;
  /**
   * Accumulate one job's outputs/failures onto the gallery. A multi-template
   * batch enqueues ONE queue of templates × designs (many jobs); each job
   * completion appends so the Gallery + Export sheet cover the whole run rather
   * than only the last job. OutputRecord ids are unique, so the Gallery key is
   * stable. `reset()` clears between runs.
   */
  appendResult(result: BatchResult, jobId?: string): void;
  /**
   * WS-H2 (AUD-3 F-15 / LB-2): drop the bytes of outputs that are already ON DISK.
   *
   * `outputs` accumulates every OutputRecord INCLUDING its bytes for the whole session, freed
   * only by the next run's `clearOutputs` — 1000 outputs x ~1.9 MB is ~1.9 GB of JS heap held
   * after the run finished. They are kept on purpose: the Export tab, the gallery and a later
   * "Save to folder" all read them. So this does not delete them — it stamps `savedRoot` on
   * every output, then drops `bytes` OLDEST-FIRST until the retained total is inside the
   * budget, keeping the newest for the gallery. Returns the bytes freed.
   *
   * DESKTOP ONLY (the caller gates on `io.kind === "desktop"`): on web there is no file to
   * read back, and breaking the Export tab is worse than holding the bytes.
   */
  spillSavedOutputs(root: string): number;
  /** Surface (or clear) a whole-run failure/cancel in the Batch panel's error banner. */
  setJobError(message: string | null): void;
  /**
   * A whole-JOB engine throw: surface `message` in the banner AND account for the failed
   * job's `expectedItems` (designs × views) that will now NEVER land in outputs/failures.
   * Without this, in a multi-template run (one job per target) the failed job's items keep
   * `outputs+failures` short of `expectedTotal` forever, so aggregate progress sticks at
   * "rendering" and the button hangs. Reducing `expectedTotal` lets `rendering` settle once
   * the still-active sibling jobs finish — while a re-click stays blocked until then, so it
   * can't clearOutputs() over their results.
   */
  failJob(message: string, expectedItems: number, jobId?: string): void;
  /**
   * WS-H1: raise `expectedTotal` by `n` because a retried / resumed segment is about to render
   * items the run total already wrote off (or never counted). WS-O: it no longer re-arms an
   * auto-save latch — the retried items reach the run folder because the writer files each one
   * as it finishes (re-writing its own path with identical bytes, H1-6), not because a
   * once-per-run save was re-armed.
   */
  addExpected(n: number, jobId?: string): void;
  reset(): void;
}

/** `slug`, or the next `<base>-N` (N ≥ 2) not in `taken`. */
function freeSlug(slug: string, taken: ReadonlySet<string>): string {
  if (!taken.has(slug)) return slug;
  const base = slug.replace(/-\d+$/, "");
  let n = 2;
  while (taken.has(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

export const useBatchStore = create<BatchState>((set) => ({
  templateId: undefined,
  versionId: undefined,
  designs: [],
  selectedDesignIds: [],
  saveDir: undefined,
  lastSavedPath: undefined,
  savedPathByJob: {},
  outputPresetName: "",
  markedSelects: [],
  views: ["back"],
  progress: { completed: 0, total: 0 },
  expectedTotal: 0,
  currentByJob: {},
  currentCompleted: 0,
  outputs: [],
  printFiles: [],
  failures: [],
  jobError: null,
  runFolder: undefined,
  replacePreviousRun: false,
  webZip: undefined,
  runReport: undefined,
  setTemplate: (templateId, versionId, views) => set({ templateId, versionId, views }),
  // A fresh design list defaults to all-selected (import = every design renders); the
  // operator then unticks what they don't want in the tree.
  setDesigns: (designs) => set({ designs, selectedDesignIds: designs.map((d) => d.designId) }),
  addDesigns: (incoming) => {
    let added = 0;
    set((s) => {
      // Identity for "already staged": the source path when we have one (a folder import
      // gives distinct paths to same-named files in different folders), else the filename.
      const key = (d: DesignInput) => `${d.bytesRef ?? ""}::${d.filename}`;
      const seen = new Set(s.designs.map(key));
      // A different file whose slug is taken (Rose.png from a second folder) is KEPT under the
      // next free `-N` slug, the run-wide de-dup's scheme — every file lands in one model
      // folder, so dropping it lost a design and keeping the slug would overwrite one.
      const taken = new Set(s.designs.map((d) => d.designSlug));
      const fresh = incoming
        .filter((d) => !seen.has(key(d)))
        .map((d) => {
          const designSlug = freeSlug(d.designSlug, taken);
          taken.add(designSlug);
          return designSlug === d.designSlug ? d : { ...d, designSlug };
        });
      added = fresh.length;
      if (fresh.length === 0) return s;
      return {
        designs: [...s.designs, ...fresh],
        // Newly added designs default to selected (import = render it); the ALREADY-staged
        // designs keep whatever the operator ticked/unticked.
        selectedDesignIds: [...s.selectedDesignIds, ...fresh.map((d) => d.designId)],
      };
    });
    return added;
  },
  setSelectedDesigns: (ids) => set({ selectedDesignIds: ids }),
  setSaveDir: (dir) => {
    set({ saveDir: dir });
    persistSaveDir(dir); // device-local only; survives restarts, never synced
  },
  setLastSavedPath: (path: string | undefined) => set({ lastSavedPath: path }),
  setJobSavedPath: (jobId, path) =>
    set((s) => (s.savedPathByJob[jobId] === path
      ? s
      : { savedPathByJob: { ...s.savedPathByJob, [jobId]: path } })),
  setOutputPreset: (name) => set({ outputPresetName: name }),
  setMarkedSelects: (keys) => set({ markedSelects: keys }),
  setReplacePreviousRun: (on) => set({ replacePreviousRun: on }),
  setWebZip: (webZip) => set({ webZip }),
  setRunReport: (runReport) => set({ runReport }),
  clearOutputs: () =>
    set((s) => ({
      runStartedAt: Date.now(),
      runEndedAt: undefined,
      outputs: [],
      printFiles: [],
      failures: [],
      progress: { completed: 0, total: 0 },
      expectedTotal: 0,
      currentByJob: {},
      currentCompleted: 0,
      // The marks referenced the PREVIOUS run's outputs; carrying them into a new run
      // would silently approve rows the operator never looked at.
      markedSelects: [],
      // Arming a run is the ONE place the export folder changes (WS-H1 / D-13). Stamping it
      // here — not per export call — is what makes the auto-save, a later "Save to folder"
      // and the Export tab land in the SAME folder for one run.
      runFolder: s.replacePreviousRun ? undefined : runFolderName(),
      // WS-O / O-8: a fresh run stages a fresh zip; the previous run's entries (and their
      // bytes, which web cannot spill) go with it.
      webZip: undefined,
      // WS-O review-1 B-1: …and the previous run's refusal tally. A stale "800 files could not
      // be written" over a fresh run is worse than none at all.
      runReport: undefined,
    })),
  setExpectedTotal: (total) =>
    set({ expectedTotal: total, currentByJob: {}, currentCompleted: 0, progress: { completed: 0, total } }),
  setProgress: (completed, total, jobId) =>
    set((s) => {
      // Per-job slot: a concurrent sibling's tick can no longer overwrite this job's count.
      const currentByJob = { ...s.currentByJob, [jobId ?? SINGLE_JOB_KEY]: completed };
      const currentCompleted = partialSum(currentByJob);
      return {
        currentByJob,
        currentCompleted,
        progress: aggregate(
          s.expectedTotal,
          s.outputs.length + s.failures.length,
          currentCompleted,
          { completed, total },
        ),
      };
    }),
  setResult: (result) =>
    set({ outputs: result.outputs, printFiles: result.printFiles ?? [], failures: result.failures }),
  appendResult: (result, jobId) =>
    set((s) => {
      // APPEND-ONLY, on purpose (review-1 L-6): a `failures` row for an item that later
      // succeeds (a retry, or a resumed segment) is NOT removed when its output lands. The
      // accounting below depends on that — `finished = outputs + failures` is reconciled
      // against an `expectedTotal` that `addExpected` grew by the retried item, so deleting
      // the row would leave the run permanently one item short of its total and hang the
      // Render button. The QUEUE record is where a recovered item stops being failed
      // (`QueueManager.reconcileRetrySource`); any future UI that renders THIS list must
      // filter rows whose `designSlug:view` also appears in `outputs`.
      const outputs = [...s.outputs, ...result.outputs];
      // Print files are deduped per (template, design) — a retried back view REPLACES its
      // earlier file instead of leaving its multi-MB bytes resident beside it (M-4).
      const printFiles = dedupePrintFiles([...s.printFiles, ...(result.printFiles ?? [])]);
      const failures = [...s.failures, ...result.failures];
      // THIS job's items are now counted in outputs/failures, so only ITS in-flight partial
      // is dropped (avoids double-counting at the boundary) — siblings keep theirs.
      const currentByJob = withoutJob(s.currentByJob, jobId ?? SINGLE_JOB_KEY);
      const currentCompleted = partialSum(currentByJob);
      return {
        outputs,
        printFiles,
        failures,
        currentByJob,
        currentCompleted,
        progress: aggregate(
          s.expectedTotal,
          outputs.length + failures.length,
          currentCompleted,
          s.progress,
        ),
      };
    }),
  spillSavedOutputs: (root) => {
    const budget = getOutputBytesBudgetMb() * 1024 * 1024;
    const s = useBatchStore.getState();
    // Stamp EVERY record first: the ones that keep their bytes still need a `savedRoot` for a
    // later spill, and the gallery uses it to re-hydrate a thumbnail.
    const stamped = s.outputs.map((o) => ({ ...o, savedRoot: root }));
    const stampedPrints: StoredPrintFile[] = s.printFiles.map((p) => ({ ...p, savedRoot: root }));
    // Walk NEWEST first, keeping bytes while they fit; everything past the budget is dropped.
    let held = 0;
    let freed = 0;
    const spill = (list: { bytes?: Uint8Array }[]) => {
      for (let i = list.length - 1; i >= 0; i--) {
        const n = list[i].bytes?.byteLength ?? 0;
        if (n === 0) continue;
        if (held + n <= budget) {
          held += n;
          continue;
        }
        freed += n;
        delete list[i].bytes;
      }
    };
    spill(stamped);
    // review-1 I-1: the rendered PRINT files are counted under the SAME budget — they are
    // outputs of this run, written to this very folder, and one photographic design at 600 DPI
    // is ~2 MB of PNG, so 500 of them is ~1 GB held outside every budget until the next run
    // arms. They spill SECOND (after the mockups) because the gallery shows the mockups and
    // never shows a print file: the visible bytes get first claim on the budget.
    //
    // Dropping them is safe now and was not before: each carries `savedRoot`, and
    // `bundle.ts::hydrateOutputBytes` re-reads `<savedRoot>/…/Print/<Design>.png` through the
    // same `readExportedFile` the mockups use. A failed re-read surfaces as a `missing` row
    // naming the root — never a silently empty `Print/`.
    spill(stampedPrints);
    set({ outputs: stamped, printFiles: stampedPrints });
    return freed;
  },
  setJobError: (message) => set({ jobError: message }),
  failJob: (message, expectedItems, jobId) =>
    set((s) => {
      // Drop the dead job's expected items from the run total so `outputs + failures` can
      // still reconcile against `expectedTotal`. Never below what has already finished.
      const finished = s.outputs.length + s.failures.length;
      const expectedTotal = Math.max(finished, s.expectedTotal - Math.max(0, expectedItems));
      // Drop only the DEAD job's in-flight partial. With the old shared scalar this
      // subtracted whatever a still-running sibling had last reported.
      const currentByJob = withoutJob(s.currentByJob, jobId ?? SINGLE_JOB_KEY);
      const currentCompleted = partialSum(currentByJob);
      // Recompute aggregate progress against the reduced total. When the run is now fully
      // accounted (no sibling job left to finish → finished >= expectedTotal) this reads
      // completed === total, so `rendering` goes false and the button releases.
      const progress =
        expectedTotal <= 0
          ? { completed: finished, total: finished }
          : {
              completed: Math.min(expectedTotal, finished + currentCompleted),
              total: expectedTotal,
            };
      return { jobError: message, expectedTotal, currentByJob, currentCompleted, progress };
    }),
  addExpected: (n, jobId) =>
    set((s) => {
      // A retried / resumed segment renders items the run total already wrote off (failJob
      // subtracted them, or they were never counted). Without this the aggregate bar reads
      // "12/12" while 3 more mockups are still rendering, and `aggregate()` clamps their
      // arrival away entirely.
      const expectedTotal = s.expectedTotal + Math.max(0, n);
      const finished = s.outputs.length + s.failures.length;
      // Drop any stale in-flight partial for this job (it belonged to the segment that just
      // ended), exactly like `appendResult`/`failJob` do.
      const currentByJob = withoutJob(s.currentByJob, jobId ?? SINGLE_JOB_KEY);
      const currentCompleted = partialSum(currentByJob);
      return {
        expectedTotal,
        currentByJob,
        currentCompleted,
        progress: { completed: Math.min(expectedTotal, finished + currentCompleted), total: expectedTotal },
      };
    }),
  reset: () =>
    set({
      designs: [],
      selectedDesignIds: [],
      outputPresetName: "",
      markedSelects: [],
      outputs: [],
      printFiles: [],
      failures: [],
      progress: { completed: 0, total: 0 },
      runStartedAt: undefined,
      runEndedAt: undefined,
      expectedTotal: 0,
      currentByJob: {},
      currentCompleted: 0,
      jobError: null,
      runFolder: undefined,
      webZip: undefined,
      runReport: undefined,
      // NOT `lastSavedPath`, which `reset()` deliberately keeps (it is where the LAST run
      // wrote). This map is the same fact per job, so it keeps them for the same reason.
    }),
}));

const rendering = (p: BatchState["progress"]) => p.total > 0 && p.completed < p.total;

/** A run ends where the Render button releases: its progress stops rendering. Every progress
 *  path (a result, a failed job, a late `addExpected`) lands here, so none can miss the stamp. */
useBatchStore.subscribe((s, prev) => {
  if (s.runStartedAt === undefined || s.runEndedAt !== undefined || s.runStartedAt !== prev.runStartedAt) return;
  if (rendering(prev.progress) && !rendering(s.progress)) useBatchStore.setState({ runEndedAt: Date.now() });
});
